import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Grid, IconButton } from '@mui/material'
import Tabs from '@mui/material/Tabs'
import { GridColDef, GridToolbarContainer } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import OrderAPI from 'api/orbitV1/OrderAPI'
import { Settings } from 'assets/icons/circles/Settings'
import CustomSearchbar from 'common/AbstractDataGrid/CustomSearchbar'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import TabButton from 'common/TabButton/TabButton'
import TabPanel from 'common/TabPanel/TabPanel'
import { ColorBoxContent } from 'common/ui/colorBox'
import { getDate } from 'helpers/date'
import { OrderGetDataBase } from 'types/api/v1/order.type'
import { OrderChangeState } from 'types/order.type'

// const ORDER_LIMIT = 50 Remove order_limit temporarily

interface IDataProps {
  title: string
  info: number
}

const AccountToolbar = () => {
  return (
    <GridToolbarContainer
      sx={{
        justifyContent: 'space-between'
      }}
    >
      <CustomSearchbar />
    </GridToolbarContainer>
  )
}

export default function PhysicalSimOrderManagement() {
  const navigate = useNavigate()

  const [retrievedOrders, setRetrievedOrders] = useState<OrderGetDataBase[]>([])
  const [tab1Orders, setTab1Orders] = useState<OrderGetDataBase[]>([])
  const [tab2Orders, setTab2Orders] = useState<OrderGetDataBase[]>([])
  const [tab3Orders, setTab3Orders] = useState<OrderGetDataBase[]>([])
  const [tab4Orders, setTab4Orders] = useState<OrderGetDataBase[]>([])

  const [dataArray, setDataArray] = useState<IDataProps[]>([])
  const [tabValue, setTabValue] = useState(0)
  const handleTabChange = (_, newNum) => setTabValue(newNum)

  const { data: getAllOrders } = useQuery(
    ['getAllOrders', 'orderManagement'],
    async () => {
      return OrderAPI.getAllOrders()
    }
  )

  useEffect(() => {
    if (getAllOrders) {
      const allOrders = getAllOrders.data
      setRetrievedOrders(allOrders)

      // Filter 'ordered' state orders
      const ordered = allOrders.filter((o) =>
        [OrderChangeState.ORDERED].includes(o.status)
      )

      const receivedOrProvisioningInProg = allOrders.filter((o) =>
        [
          OrderChangeState.PAYMENT_RECEIVED,
          OrderChangeState.PREPARING
        ].includes(o.status)
      )
      const readyOrScheduledForDelivery = allOrders.filter((o) =>
        [
          OrderChangeState.READY_FOR_DELIVERY,
          OrderChangeState.SCHEDULED_FOR_DELIVERY
        ].includes(o.status)
      )

      const remaining = allOrders.filter(
        (o) =>
          // negation of above
          ![
            OrderChangeState.ORDERED,
            OrderChangeState.PAYMENT_RECEIVED,
            OrderChangeState.PREPARING,
            OrderChangeState.READY_FOR_DELIVERY,
            OrderChangeState.SCHEDULED_FOR_DELIVERY
          ].includes(o.status)
      )

      setTab1Orders(ordered)
      setTab2Orders(receivedOrProvisioningInProg)
      setTab3Orders(readyOrScheduledForDelivery)
      setTab4Orders(remaining)
      setDataArray([
        { title: 'All', info: allOrders.length },
        { title: 'Pending Payment', info: ordered.length },
        {
          title: 'For Provisioning',
          info: receivedOrProvisioningInProg.length
        },
        {
          title: 'For Delivery Schedule / Pickup',
          info: readyOrScheduledForDelivery.length
        },
        { title: 'On the Way / Delivered', info: remaining.length }
      ])
    }
  }, [getAllOrders])

  const columns: GridColDef[] = [
    { field: 'orderNo', headerName: 'Order No.', width: 100 },
    { field: 'orgName', headerName: 'Customer', width: 100 },
    { field: 'userEmail', headerName: 'Email', width: 100 },
    {
      field: 'quantity',
      headerName: 'No. of SIMs',
      width: 50,
      valueGetter: (params) => {
        const order: OrderGetDataBase = params.row
        const quantity = order.items.reduce(
          (prevCount, currItem) => prevCount + currItem.quantity,
          0
        )
        return quantity
      }
    },
    { field: 'status', headerName: 'Status', width: 50 },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 50,
      valueGetter: (params) => {
        const order: OrderGetDataBase = params.row
        return getDate(order.createdAt)
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 50,
      renderCell: (params) => {
        const order: OrderGetDataBase = params.row
        return (
          <IconButton
            // TO DO : to change this route to new route
            onClick={() =>
              navigate(`/hq/manage-orders/individual-order/${order.orderNo}`)
            }
            data-cy='actionsBtn'
          >
            <Settings />
          </IconButton>
        )
      }
    }
  ]

  const variantPicker = (allocatedNum) =>
    allocatedNum === tabValue ? 'filled' : 'tertiary'
  const colorPicker = (allocatedNum) =>
    allocatedNum === tabValue ? 'white' : 'black'

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <ColorBoxContent>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{
              style: { display: 'none' }
            }}
          >
            {dataArray.map((label, index) => {
              return (
                <TabButton
                  key={label.title}
                  label={`${label.title} (${label.info})`}
                  color={colorPicker(index)}
                  variant={variantPicker(index)}
                  value={index}
                  onClick={(num) => setTabValue(num)}
                />
              )
            })}
          </Tabs>
        </ColorBoxContent>
      </Grid>

      <Grid item>
        {[retrievedOrders, tab1Orders, tab2Orders, tab3Orders, tab4Orders].map(
          (orders, index) => {
            return (
              <TabPanel
                key={`OrderManagementPage-tab${index}`}
                value={tabValue}
                index={index}
              >
                <ColorBoxContent>
                  <MUIDataGrid
                    getRowId={(order: OrderGetDataBase) =>
                      order.orderNo + order._id
                    }
                    initialState={{
                      sorting: {
                        sortModel: [{ field: 'createdAt', sort: 'desc' }]
                      }
                    }}
                    rows={orders}
                    columns={columns}
                    components={{ Toolbar: AccountToolbar }}
                  />
                </ColorBoxContent>
              </TabPanel>
            )
          }
        )}
      </Grid>
    </Grid>
  )
}
